import type {
  Equipment,
  SwitchingDetailed,
  SwitchingRead,
  EquipmentRead,
  EquipmentWithActivatedDeactivated,
  ProjectRead,
} from '@gridcon-energy/schaltungstool-backend'
import { SwitchingCategoryTypeEnum } from '@gridcon-energy/schaltungstool-backend'
import { SVGEquipment } from '~/composables/svg/useSVGColoring'
import type { EarliestLatestDates } from '~/lib/switchings'
import { isActivatedOrDeactivatedType, sortAscending } from '~/lib/switchings'
import { CSSStyle } from '~/types'

export enum EquipmentStatusEnum {
  EXISTING = 'bestand', // no IBN/ABN (default)
  ADDITION = 'zubau', // only IBN (Not available at start, available at end)
  DISMANTLING = 'abbau', // only ABN (Available at start, not available at end)
  TEMP_AVAILABLE = 'temporaer_verfuegbar', // (IBN -> ABN) Not available at start and end, but available in between
  // TEMP_NOT_AVAILABLE = 'temporaer_nicht_verfuegbar', // (ABN -> IBN) Available at start and end, but not available in between
  UNKNOWN = 'unbekannt', // e.B no switchings
}

export function isEquipmentStatusColorable(status: EquipmentStatusEnum): boolean {
  return ![EquipmentStatusEnum.EXISTING, EquipmentStatusEnum.UNKNOWN].includes(status)
}
export const EquipmentStatusColorable = Object.values(EquipmentStatusEnum).filter(isEquipmentStatusColorable)
export const EquipmentStatusPlain = Object.values(EquipmentStatusEnum).filter((s) => !isEquipmentStatusColorable(s))

export interface EquipmentEventDates {
  first?: SwitchingRead
  last?: SwitchingRead
  count: null | number
  multiple: boolean
  events: SwitchingRead[]
}

export type EquipmentEventsByType = Record<SwitchingCategoryTypeEnum, EquipmentEventDates>
export type EquipmentExtended = EquipmentRead & {
  // Calculated fields
  status: EquipmentStatusEnum | null // null if not possible to determine
  events: EquipmentEventsByType

  // Presence flags
  inSVG: boolean
  hasSwitchings: boolean

  inSVGData?: SVGEquipment
}

export function asEquipmentExtended(item: any): EquipmentExtended {
  return item as EquipmentExtended
}

export function isEquipmentRead(item: Equipment): item is EquipmentRead {
  return 'id' in item
}

/**
 * Calculate equipment status based on switching events within the activity time span
 */
export function calculateEquipmentStatus(
  equipmentSwitchings: SwitchingDetailed[] = [],
  activityStartEnd?: EarliestLatestDates,
): EquipmentStatusEnum | null {
  const events: SwitchingDetailed[] = equipmentSwitchings.slice()

  if (events.length === 0 || !activityStartEnd) {
    return null
  }
  const { earliest, latest } = activityStartEnd
  events.sort(sortAscending)

  const firstEvent = events[0]
  const lastEvent = events[events.length - 1]

  // TODO: consolidate with "offline bars" in Gantt view?
  const isAvailableAtStart =
    firstEvent.switching_category_type !== SwitchingCategoryTypeEnum.Activated &&
    earliest.isSameOrBefore(firstEvent.start, 'day')
  const isAvailableAtEnd =
    lastEvent.switching_category_type !== SwitchingCategoryTypeEnum.Deactivated &&
    latest.isSameOrAfter(lastEvent.end, 'day')

  if (isAvailableAtStart && isAvailableAtEnd) {
    return EquipmentStatusEnum.EXISTING
  } else if (!isAvailableAtStart && isAvailableAtEnd) {
    return EquipmentStatusEnum.ADDITION
  } else if (isAvailableAtStart && !isAvailableAtEnd) {
    return EquipmentStatusEnum.DISMANTLING
  } else if (!isAvailableAtStart && !isAvailableAtEnd) {
    return EquipmentStatusEnum.TEMP_AVAILABLE
  } else {
    return EquipmentStatusEnum.UNKNOWN
  }
}

/**
 * Calculate event dates and counts for each switching type
 */
export function calculateEquipmentEvents(equipmentSwitchings: SwitchingDetailed[]): EquipmentEventsByType {
  const equipmentsByType = {} as EquipmentEventsByType
  for (const category of Object.values(SwitchingCategoryTypeEnum)) {
    equipmentsByType[category] ??= {
      first: undefined,
      last: undefined,
      count: null,
      multiple: false,
      events: [],
    }
  }

  for (const event of equipmentSwitchings) {
    const category = event.switching_category_type
    const count = (equipmentsByType[category].count ?? 0) + 1
    equipmentsByType[category].events.push(event)
    equipmentsByType[category].count = count
    equipmentsByType[category].multiple = count > 1
    equipmentsByType[category].first = equipmentsByType[category].first || event
    equipmentsByType[category].last = event
  }
  return equipmentsByType
}

/**
 * Map of equipment ids to their corresponding SVG element
 *
 * Caveat: `matches` is a name match only - a layer can be found here with an
 * empty `elements` array (mapped but no geometry). Use `svgEquipmentHasElements`
 * on the result to check whether the equipment is actually usable/visible in
 * the SVG, don't treat presence in `matches` as sufficient on its own.
 */
export function matchEquipmentsInSVG(equipments: EquipmentWithActivatedDeactivated[], SVGEquipments: SVGEquipment[]) {
  const matches = new Map<number, SVGEquipment>()
  const onlySVG = new Map<string, SVGEquipment>()
  const onlyDB = new Map<number, EquipmentWithActivatedDeactivated>()

  const _equipments = equipments.slice()

  // Matches + only SVG
  for (const svgEquipment of SVGEquipments) {
    const matchingIndex = _equipments.findIndex((e) => e.name === svgEquipment.name)
    if (matchingIndex > -1) {
      const equipment = _equipments.splice(matchingIndex, 1)[0]
      matches.set(equipment.id, svgEquipment)
    } else {
      onlySVG.set(svgEquipment.name, svgEquipment)
    }
  }

  // Only DB
  for (const equipment of _equipments) {
    onlyDB.set(equipment.id, equipment)
  }
  return {
    matches,
    onlySVG,
    onlyDB,
  }
}
export enum NetworkDisplayModeEnum {
  start = 'start',
  end = 'end',
  topology = 'topology',
  equipment_status = 'equipment_status',
}

export enum EquipmentNetworkStateEnum {
  ONLINE = 'online', // ...and affected by switchings
  OFFLINE = 'offline',
}

export type EquipmentNetworkState = {
  equipment: EquipmentRead
  state: EquipmentNetworkStateEnum
}
export type EquipmentWithColorInfo = {
  equipment: EquipmentRead
  cssClass: string[]
  style: CSSStyle
  projects: ProjectRead[]
}
